import { css } from '@emotion/react';
import tw, { TwStyle, theme } from 'twin.macro';
import React, { ReactElement } from 'react';
import { CloseIcon } from '$/images/Icons';
import { isNumber } from '$/utils/number';
import useRunOnce from '$/hooks/useRunOnce';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import isMobile from '$/utils/isMobile';
import { PermissionedLink } from '../Router/PermissionedLink';
import { withProperties } from '$/utils/type';
import Button from './Button';

interface ModalContextProps {
  id: string;
  hasHeader: boolean;
  setHasHeader: (value: boolean) => void;
}

export const ModalContext = React.createContext<ModalContextProps>({} as ModalContextProps);
const useModalContextProvider = (id: string): ModalContextProps => {
  const [hasHeader, setHasHeader] = React.useState(false);
  return {
    id,
    hasHeader,
    setHasHeader,
  };
};

const ModalContextProvider: React.FC<{ id: string; children?: React.ReactNode }> = ({
  children,
  id,
}) => {
  const modalContextProvider = useModalContextProvider(id);
  return <ModalContext.Provider value={modalContextProvider}>{children}</ModalContext.Provider>;
};

function useOnClickOutside(cb: (target: HTMLDivElement) => void): React.Ref<HTMLDivElement> {
  const node = React.useRef<HTMLDivElement>(null);

  useRunOnce(() => {
    const handleClick = (e: MouseEvent): void => {
      const target = e.target as HTMLDivElement;
      const clickInside = node.current! && node.current!.contains(target);
      if (cb && !clickInside) {
        // inside click
        return cb(target);
      }
      // outside click
    };
    document.addEventListener('mousedown', handleClick);
    // return function to be called when unmounted
    return (): void => {
      document.removeEventListener('mousedown', handleClick);
    };
  }); // Should include cb but cb should always be a React.Callback first

  return node;
}

const focusableElsQueryString = `
  a[href]:not([tabindex='-1']):not([disabled]), 
  button:not([tabindex='-1']):not([disabled]), 
  textarea:not([tabindex='-1']):not([disabled]), 
  input:not([tabindex='-1']):not([disabled]):not([type='hidden']),
  select:not([tabindex='-1']):not([disabled]),
  [tabindex]:not([tabindex='-1'])
`;
function useLockFocusInside(): React.RefObject<HTMLDivElement> {
  const node = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const element = node.current;
    if (!element) {
      return;
    }

    // Handle special cases like .selected-flag and .no-focus which should not be focusable
    const noFocusEls = element.querySelectorAll<HTMLElement>('.selected-flag, .no-focus');
    noFocusEls.forEach((el) => {
      el.setAttribute('tabindex', '-1');
    });

    const focusableEls = element.querySelectorAll<HTMLElement>(focusableElsQueryString);
    if (focusableEls.length === 0) return;

    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    const onKeyDownEvent = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    };

    element.addEventListener('keydown', onKeyDownEvent);
    return () => {
      element.removeEventListener('keydown', onKeyDownEvent);
    };
  }, []); // Empty dependency array means this effect will only run once after the first render

  return node;
}

function useCloseOnEscape(onClose?: (target: HTMLElement) => void): void {
  const { id } = React.useContext(ModalContext);
  React.useEffect(() => {
    const onKeyDownEvent = (e: KeyboardEvent): void => {
      const isEscapePressed = e.key === 'Escape' || e.key === 'Esc';
      if (isEscapePressed) {
        const modalRoot = document.getElementById(id);
        const modalContainer = document.getElementById('modalContainer') as HTMLDivElement;
        if (modalRoot && modalContainer && modalContainer.contains(modalRoot)) {
          const allModalRoots = modalContainer.querySelectorAll('.modal-root.fixed');
          const latestActiveModal = allModalRoots.item(allModalRoots.length - 1);
          if (modalRoot === latestActiveModal) {
            return onClose?.(e.target as HTMLElement);
          }
        } else {
          return onClose?.(e.target as HTMLElement);
        }
      }
    };
    window.addEventListener('keydown', onKeyDownEvent);
    return (): void => {
      window.removeEventListener('keydown', onKeyDownEvent);
    };
  }, [id, onClose]);
}

export const CloseOnEscape: React.FC<{
  onClose?: (target: HTMLElement) => void;
}> = ({ onClose }) => {
  useCloseOnEscape(onClose);
  return null;
};

function useLockBodyScroll(lock: boolean): void {
  useRunOnce(() => {
    // Get original body overflow
    //const originalStyle = window.getComputedStyle(document.body).overflow;

    // Prevent scrolling on mount
    if (lock) {
      document.body.style.overflow = 'hidden';
    }

    // Re-enable scrolling when component unmounts
    return (): void => {
      document.body.style.overflow = 'auto';
    };
  });
}

interface ModalProps {
  onClose?: () => void;
  anchorToTop?: boolean | number;
  lock?: boolean;
  closeOnClickOutside?: boolean;
  modalWrapClassName?: string;
  className?: string;
  widthClass?: string;
  backdropClassName?: string;
  hide?: boolean;
  notFullscreen?: boolean;
  id?: string;
  link?: string;
  formMode?: boolean;
  children?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  hide,
  lock = true,
  children,
  notFullscreen,
  backdropClassName = '',
  anchorToTop,
  closeOnClickOutside,
  className = '',
  modalWrapClassName,
  widthClass = '',
  onClose,
  id,
  link,
  formMode,
}) => {
  const [modalId] = React.useState(id || `modal-${new Date(Date.now()).getTime()}`);
  const ref = useOnClickOutside(closeOnClickOutside && onClose ? onClose : (): void => {});
  const [blurClass, setBlurClass] = React.useState('');
  const { height } = useWindowSizeInfo();

  const modalRef = useLockFocusInside();
  useLockBodyScroll(!hide && lock);

  React.useEffect(() => {
    setBlurClass(hide ? '' : 'showblur');
  }, [hide]);

  const wrapperClassName = hide
    ? 'hidden'
    : `fixed top-0 left-0 w-full h-full md:h-100vh ${backdropClassName} ${blurClass}`;

  return (
    <ModalContextProvider id={modalId}>
      <div
        id={modalId}
        className={`${wrapperClassName} modal-root`}
        css={css`
          ${tw`z-modal`}
          transition:
            backdrop-filter 0.05s,
            background-color 0.05s;
          background: rgba(0, 0, 0, 0);
          &.showblur {
            background: rgba(0, 0, 0, 0.25);
            backdrop-filter: blur(2px);
          }
          ul,
          ol {
            margin: 1rem 0 1rem 2rem;
          }
          ul {
            list-style-type: disc;
          }
          ol {
            list-style: decimal;
          }
        `}
        ref={modalRef}
      >
        {formMode && (
          <PermissionedLink
            id='unsavedDialog'
            to={link || '/home'}
            onClick={(e: React.MouseEvent<HTMLAnchorElement>): void => {
              if (onClose) {
                e.preventDefault();
                onClose();
              }
            }}
          />
        )}
        <div
          className={`mx-auto flex flex-column h-full w-full z-100 ${modalWrapClassName ?? ''}`}
          css={css`
            ${anchorToTop ? 'align-items:start;' : 'align-items:center;'}
          `}
        >
          <div
            ref={ref}
            className={`Modal flex flex-col ${widthClass} ${className} `}
            css={css`
              ${tw`bg-white shadow-2xl mx-auto rounded-lg relative md:min-w-30e max-w-40e`}
              max-height: ${isMobile && !notFullscreen ? '100%' : 'calc(100vh - 2rem)'};
              ${isMobile && !notFullscreen ? `height: ${height}px; border-radius: 0;` : ''}
              ${anchorToTop
                ? `margin-top: ${typeof anchorToTop === 'number' ? anchorToTop : 5}%;`
                : ''}
              overflow-x: scroll;
            `}
          >
            {children}
          </div>
        </div>
      </div>
    </ModalContextProvider>
  );
};
interface TitleProps {
  children?: React.ReactNode;
  simple?: boolean;
  className?: string;
}
const ModalTitle = React.forwardRef<HTMLDivElement, TitleProps>(
  ({ simple, children, className = '' }, ref) => {
    return (
      <div
        ref={ref}
        className={`${className} modal-title md:rounded-t-lg`}
        css={css`
          display: flex;
          flex-direction: row;
          line-height: 1.5;
          ${tw`py-1/4e`}
          ${simple ? tw`border-b bg-blue-1` : tw`text-xl`};
        `}
      >
        {children}
      </div>
    );
  }
);

const ModalBody: React.ForwardRefExoticComponent<
  {
    className?: string | undefined;
    noFooter?: boolean | undefined;
    children?: React.ReactNode;
  } & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, { className?: string; children?: React.ReactNode }>(
  ({ children, className = '' }, ref) => {
    // make scrollbar of ModalBody not over Modal
    const { hasHeader } = React.useContext(ModalContext);
    return (
      <div
        className={className}
        ref={ref}
        css={css`
          ${tw`px-1e py-2e sm:px-2e overflow-x-visible overflow-y-auto`}
          height: 100%;
          ${!hasHeader ? `margin-top: 1rem;` : ``}
        `}
      >
        {children}
      </div>
    );
  }
);
interface ModalFooterProps {
  gray?: boolean;
  className?: string;
  sticky?: boolean;
  children?: React.ReactNode;
}
const ModalFooter: React.FC<ModalFooterProps> = ({
  gray,
  children,
  className,
  sticky = true,
}): ReactElement => (
  <div
    className={`modal-footer ${sticky && 'sticky bottom-0 mt-auto'} ${className}`}
    css={css`
      ${tw`md:rounded-b-lg border-t border-gray-200 px-1e sm:px-2e`}
      background: ${gray ? theme`colors.gray.200`.toString() : '#fff'};
      padding-top: ${isMobile ? '1.5rem' : gray ? '2rem' : '1.25rem'};
      padding-bottom: ${isMobile ? '1.5rem' : gray ? '2rem' : '1.25rem'};
    `}
  >
    {children}
  </div>
);

interface CloseProps {
  onClick?: () => void;
  className?: string;
  formMode?: boolean;
}
const ModalClose: React.FC<CloseProps> = ({ onClick, className, formMode }): ReactElement => {
  const triggerOnEscape = React.useCallback((): void => {
    onClick && onClick();
    if (formMode) {
      const unsavedDialog = document.getElementById('unsavedDialog');
      unsavedDialog && unsavedDialog.click();
    }
  }, [formMode, onClick]);
  useCloseOnEscape(triggerOnEscape);
  return (
    <Button
      onClick={triggerOnEscape}
      className={`${className} button-control modal-close`}
      css={css`
        ${tw`appearance-none outline-none`}
        align-self: top;
        margin-right: 4px;
      `}
    >
      <CloseIcon className='w-4 h-4 transition-tranform duration-100' />
    </Button>
  );
};

interface CustomLinkProps {
  formMode?: boolean;
  onClick?: () => void;
  id?: string;
  className?: string;
  as?: React.FC | string;
  children?: React.ReactNode;
}

const ModalCustomLink: React.FC<CustomLinkProps> = ({
  children,
  formMode,
  onClick,
  as,
  ...props
}): ReactElement => {
  const As = as ?? Button;

  const onClose = (): void => {
    onClick && onClick();
    if (formMode) {
      const unsavedDialog = document.getElementById('unsavedDialog');
      unsavedDialog && unsavedDialog.click();
    }
  };

  return (
    <As className='text-gray-1 my-2' onClick={onClose} {...props}>
      {children}
    </As>
  );
};

interface HeaderProps {
  className?: string;
  gray?: boolean | TwStyle;
  delete?: boolean;
  color?: string;
  sticky?: boolean;
  top?: number;
  children?: React.ReactNode;
}
const ModalHeader: React.FC<HeaderProps> = ({
  children,
  className,
  gray,
  delete: isDelete,
  color = isDelete ? '#fff' : 'black',
  sticky,
}): JSX.Element => {
  const { setHasHeader } = React.useContext(ModalContext);
  useRunOnce(() => {
    setHasHeader(true);
    return (): void => setHasHeader(false);
  });

  return (
    <div
      className={`modal-header ${className}`}
      css={[
        isDelete
          ? tw`bg-red-1 text-xl py-3`
          : gray
            ? typeof gray !== 'boolean'
              ? gray
              : tw`bg-gray-200 py-3`
            : tw`bg-white`,
        sticky && tw`top-0 sticky`,
        tw`items-center px-2e flex justify-between md:rounded-t-lg flex-shrink-0`,
        css`
          > .modal-close {
            position: absolute;
            right: 1rem;
            top: 1.5rem;
          }
          @media (min-width: 767px) {
            > .modal-title {
              padding-right: 2rem;
            }
          }
          color: ${color};
          button {
            &.modal-close {
              > svg {
                color: ${color} !important;
              }
            }
            &:hover:not([disabled]) {
              transform: scale(1.1);
              &.modal-close {
                & > svg {
                  color: ${color} !important;
                }
              }
            }
            svg {
              color: ${color};
            }
          }
        `,
      ]}
    >
      {children}
    </div>
  );
};

interface StickyButtonHeaderProps {
  className?: string;
  colorButton?: string;
  shadow?: boolean;
  children?: React.ReactNode;
}

// This component use for buttons always put at the corner right top of modal - like Thinkific modal
const ModalStickyButtonHeader: React.FC<StickyButtonHeaderProps> = ({
  children,
  className,
  colorButton = 'black',
  shadow,
}): ReactElement => {
  return (
    <div
      className={className}
      css={css`
        ${tw`flex absolute items-center z-60`}
        top: 1.5rem;
        height: 0.75rem;
        right: 0.5rem;
      `}
    >
      <div
        className='flex ml-auto rounded-full items-center'
        css={css`
          ${shadow
            ? `
            backdrop-filter: blur(2px);
            background-color: rgba(0, 0, 0, 0.4);
          `
            : ''}
          padding: 0.5rem 1rem;
          > * {
            margin-right: 1rem;
            &:last-child {
              margin-right: 0;
            }
          }
          .button-control {
            &.modal-close {
              > svg {
                color: ${colorButton} !important;
              }
            }
            &:hover:not([disabled]) {
              svg {
                transform: scale(1.1);
              }
              &.modal-close {
                > svg {
                  color: ${colorButton} !important;
                }
              }
            }
            svg {
              color: ${colorButton};
            }
          }
        `}
      >
        {children}
      </div>
    </div>
  );
};
interface StickyWrapperBodyProps {
  children?: React.ReactNode;
  className?: string;
  sticky?: boolean;
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

// This component use for any element want to sticky inside body element
const ModalStickyWrapperBody: React.FC<StickyWrapperBodyProps> = ({
  children,
  className,
  sticky,
  top,
  bottom,
}) => {
  return (
    <div
      className={`${className} z-60`}
      css={css`
        ${sticky
          ? css`
              position: sticky !important;
              ${isNumber(top) ? `top: ${top}rem;` : ''}
              ${isNumber(bottom) ? `bottom: ${bottom}rem;` : ''}
            `
          : ''}
      `}
    >
      {children}
    </div>
  );
};

interface ScrollableProps {
  children?: React.ReactNode;
  className?: string;
}

const ModalScrollable: React.FC<ScrollableProps> = ({ children, className }): JSX.Element => {
  return <div className={`${className} overflow-y-auto`}>{children}</div>;
};
interface ModalLandscapeProps {
  className?: string;
  id?: string;
  onMobileShow?: 'left' | 'right';
  children?: React.ReactNode;
}

const ModalLandscape: React.FC<ModalLandscapeProps> = ({
  children,
  className,
  id,
  onMobileShow,
}): JSX.Element => {
  let els = children;

  if (isMobile) {
    els = onMobileShow === 'left' ? (children as any)[0] : (children as any)[1];
  }

  return (
    <div
      id={id}
      className={`${className} flex flex-row`}
      css={
        !isMobile
          ? undefined
          : css`
              > div {
                width: 100%;
              }
            `
      }
    >
      {els}
    </div>
  );
};

interface ModalPreviewProps {
  className?: string;
  children?: React.ReactNode;
}
const ModalPreview: React.FC<ModalPreviewProps> = ({ children, className }): JSX.Element => {
  return (
    <div
      className={`${className} rounded-tl-lg`}
      css={css`
        width: 40%;
        min-width: 425px;
      `}
    >
      {children}
    </div>
  );
};

interface ModalContentProps {
  id?: string;
  className?: string;
  children?: React.ReactNode;
}
const ModalContent: React.FC<ModalContentProps> = ({ id, children, className }): JSX.Element => {
  return (
    <div
      id={id}
      className={`${className} rounded-tr-lg`}
      css={css`
        width: 60%;
      `}
    >
      {children}
    </div>
  );
};
export default withProperties(Modal, {
  Title: ModalTitle,
  Header: ModalHeader,
  Body: ModalBody,
  StickyButtonHeader: ModalStickyButtonHeader,
  StickyWrapperBody: ModalStickyWrapperBody,
  Close: ModalClose,
  Scrollable: ModalScrollable,
  Footer: ModalFooter,
  CustomLink: ModalCustomLink,
  Preview: ModalPreview,
  Landscape: ModalLandscape,
  Content: ModalContent,
});
